import { useAtomValue, useSetAtom } from 'jotai'
import { useCallback } from 'react'
import { useModals, useSetChatInput } from '../../../../../store'
import { WindowSelectionType } from '../../../../../store/atoms'
import { useAddWindow } from '../../../../../hooks/useAddWindow'
import { logAmplitudeEvent } from '../../../../../utils'
import { MentionableItem } from '../../../../../types'
import { closeMentionsAtom, mentionSearchAtom, slashModeAtom, useCursorPosition, useMessageMentions } from '../atoms'
import { useMentionsHandler } from './use-mentions-handler'
import { usePaidMentionGate } from './use-paid-mention-gate'

/**
 * What happens when a mention is chosen: the paywall gates first (a blocked
 * pick opens a modal and never reaches the input), then the label is spliced
 * into the draft in place of the `@`/`/` query the user typed.
 */
export const useSelectMention = () => {
  const mentionSearch = useAtomValue(mentionSearchAtom)
  const slashMode = useAtomValue(slashModeAtom)
  const closeMentions = useSetAtom(closeMentionsAtom)
  const setChatInput = useSetChatInput()
  const cursorPosition = useCursorPosition()
  const messageMentions = useMessageMentions()
  const { selectMention } = useMentionsHandler()
  const { openModal } = useModals()
  const gatePaidMention = usePaidMentionGate()
  const onAddWindow = useAddWindow()

  return useCallback(
    (mention: MentionableItem) => {
      if (!mention) {
        return
      }

      if (gatePaidMention(mention, 'chat mention')) {
        closeMentions()
        return
      }

      const triggerChar = slashMode ? '/' : '@'

      if (mention.type === 'browser_tab') {
        const tabAsWindow: WindowSelectionType = {
          appName: 'Tab',
          appIcon: mention.images?.image,
          pid: -1,
          windowTitle: mention.label,
          tabId: mention.id,
        }

        onAddWindow(tabAsWindow)

        // A tab becomes a context chip, not text — drop the query it was picked from.
        setChatInput((prev) => {
          const mentionStart = prev.lastIndexOf(triggerChar)
          if (mentionStart === -1) return prev
          return prev.slice(0, mentionStart) + prev.slice(mentionStart + mentionSearch.length + 1)
        })
        closeMentions()
        return
      }

      selectMention(mention)

      const injectMention = (input: string, label: string) => {
        // In slash mode: keep the `/` prefix so the slash command stays visible.
        if (slashMode) {
          const slashAt = input.slice(0, cursorPosition).lastIndexOf('/')
          if (slashAt === -1) {
            const before = input.slice(0, cursorPosition)
            const after = input.slice(cursorPosition)
            return before + (before.endsWith(' ') || before.length === 0 ? '' : ' ') + '/' + label + ' ' + after
          }
          const before = input.slice(0, slashAt)
          const after = input.slice(slashAt + 1 + mentionSearch.length)
          const appended = '/' + label + (after.startsWith(' ') ? '' : ' ')
          return before + appended + after
        }

        // Check if there's no @ symbol or if all @ symbols are already used in mentions.
        // Only count `@`-prefixed mentions — slash mentions live in messageMentions too but don't consume @s.
        const atSymbolCount = (input.match(/@/g) || []).length
        const atMentionCount = messageMentions.filter((m) => input.includes(`@${m.label}`)).length
        if (input.indexOf('@') === -1 || atSymbolCount === atMentionCount) {
          // Inject at current cursor position with @ symbol
          const before = input.slice(0, cursorPosition)
          const after = input.slice(cursorPosition)
          return before + (before.endsWith(' ') ? '' : ' ') + '@' + label + ' ' + after
        }

        // Normal case - inject at cursor position
        const appendAt = input.slice(0, cursorPosition).lastIndexOf('@') + 1
        const before = input.slice(0, appendAt)
        const after = input.slice(appendAt).replace(mentionSearch, '')
        const appended = label + (after.startsWith(' ') ? '' : ' ')
        return before + appended + after
      }

      setChatInput((prev) => injectMention(prev, mention.label))

      logAmplitudeEvent('Chat Mention Selected', {
        mention_type: mention.type,
        mention_label: mention.label,
        source: window.__RENDERER_NAME,
        has_other_mentions: messageMentions.length > 0,
        is_pro_model: mention.type === 'model' && mention.pricing?.isFree === false,
        is_experimental_model: mention.type === 'model' && mention.meta?.experimental === true,
      })
    },
    [
      selectMention,
      setChatInput,
      closeMentions,
      mentionSearch,
      cursorPosition,
      messageMentions,
      openModal,
      onAddWindow,
      slashMode,
      gatePaidMention,
    ],
  )
}
